import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Tabs,
  Tab,
  Box,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { ArrowRightAlt, CameraOutdoorOutlined } from "@mui/icons-material";

import TemplatePreview from "../../widgets/TemplatePreview";
import { LanguageAutocomplete } from "../../widgets/LanguageAutocomplete";
import HeaderOptions from "./HeaderOptions";
import TemplateBody from "./TemplateBody";
import ButtonTemplateCreator from "./ButtonOptions";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import ConstructionIcon from "@mui/icons-material/Construction";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateAuthenticationTemplate from "./CreateAuthenticationTemplate";
import { useDialog } from "../../context/DialogContext";
import LoadingWidget from "../../widgets/LoadingWidget";
import WhatsAppCarouselCreator from "./WhatsAppCarouselCreator";
import { GlassButtonLoading } from "../../themes/UiElements";
export const CATEGORIES = [
  { type: "Marketing", icon: <CameraOutdoorOutlined /> },
  { type: "Utility", icon: <ConstructionIcon /> },
  { type: "Authentication", icon: <KeyOutlinedIcon /> },
  { type: "Media Carousel", icon: <CameraOutdoorOutlined /> },
];

const OptionalHeader = ({ label, subLabel }) => (
  <Typography variant="h6" gutterBottom>
    {label}
    <span style={{ fontWeight: "normal", fontSize: "small" }}>{subLabel}</span>
  </Typography>
);

function CreateSubmitTemplate() {
  const [searchParams] = useSearchParams();
  const tid = searchParams.get("tid");
  const p = searchParams.get("p");
  const pNumber = p ? (isNaN(p) ? 0 : parseInt(p)) : 0;
  const { showDialog, showInputDialog } = useDialog();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(pNumber);
  const [lanCode, setLanCode] = useState("en_US");
  const [varType, setVarType] = useState("POSITIONAL");
  const [templateName, setTemplateName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [approveForAll, setApproveForAll] = useState(false);

  // header, body, footer, buttons
  const [header, setHeader] = useState({ type: "NONE" });
  const [bodyText, setBodyText] = useState("");
  const [bodyParams, setBodyParams] = useState({});
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState([]);
  /* ----------------------- derive components[] ----------------------- */
  const components = useMemo(() => {
    const comps = [];
    /* HEADER */
    if (header.type !== "NONE") {
      const comp = { type: "HEADER", format: header.type };
      switch (header.type) {
        case "TEXT":
          comp.text = header.text ?? "";
          if (header.param) {
            comp.example = {
              header_text: [header.param],
            };
          }
          break;
        default:
      }
      comps.push(comp);
    }
    /* BODY */
    if (bodyText.trim()) {
      const body = { type: "BODY", text: bodyText };
      const vars = Object.values(bodyParams).filter(Boolean);
      if (vars.length) {
        body.example = { body_text: [vars] };
      }
      comps.push(body);
    }
    if (footerText.trim()) comps.push({ type: "FOOTER", text: footerText });
    if (buttons.length) {
      const buttonBlocks = buttons.map((b) => {
        switch (b.type) {
          case "url":
            const urlButton = {
              type: "URL",
              text: b.text,
              url: b.url,
            };

            // Add example if dynamic URL with variable
            if (b.isDynamic && b.urlVariable?.example) {
              urlButton.url = `${b.url}?${b.urlVariable.name}={{1}}`;
              urlButton.example = `${b.url}?${b.urlVariable.name}=${b.urlVariable.example}`;
            }

            return urlButton;

          case "phone":
            return {
              type: "PHONE_NUMBER",
              text: b.text,
              phone_number: b.phone,
            };

          case "code":
            return {
              type: "COPY_CODE",
              code: b.code,
            };

          case "flow":
            return {
              type: "FLOW",
              text: b.text,
              flow_id: b.flowId,
            };

          default: // quick replies
            return { type: "QUICK_REPLY", text: b.text };
        }
      });
      comps.push({ type: "BUTTONS", buttons: buttonBlocks });
    }
    return comps;
  }, [header, bodyText, bodyParams, footerText, buttons]);

  /* ----------------------- final payload (memo) ----------------------- */
  const payload = useMemo(
    () => ({
      name: templateName.trim(),
      category: CATEGORIES[activeTab].type.toUpperCase(),
      language: lanCode,
      components,
    }),
    [templateName, activeTab, lanCode, components]
  );

  useEffect(() => {
    setActiveTab(pNumber);
  }, [pNumber]);
  useEffect(() => {
    if (!tid) return;
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_ENDPOINT}/api/get-template/${tid}`,
          getHeader()
        );
        const tdata = response.data;
        setTemplateName(tdata.name);
        setLanCode(tdata.language);
        const header = tdata.components.find((item) => item.type === "HEADER");
        const body = tdata.components.find((item) => item.type === "BODY");
        const footer = tdata.components.find((item) => item.type === "FOOTER");
        setBodyText(body.text);
        setVarType(tdata.parameter_format);
        setHeader(header ? { type: header.format } : {});
        const cat = tdata.category;
        const ind = CATEGORIES.findIndex(
          (item) => item.type.toUpperCase() === cat
        );
        setActiveTab(ind);
        if (footer) setFooterText(footer.text);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [tid]);

  /* ----------------------- submit handler ----------------------- */
  const handleSubmit = useCallback(async () => {
    if (!payload.name || !bodyText.trim()) {
      toast.error("Template name and body text are required");
      return;
    }

    if (/{{\s*[^}]+\s*}}$/.test(bodyText.trim())) {
      toast.error("Body text should not end with a variable placeholder");
      return;
    }
    if (!tid) {
      try {
        setSubmitting(true);
        const response = await axios.post(
          `${API_ENDPOINT}/api/create-template-for-approval`,
          { ...payload, approveForAll },
          getHeader()
        );
        showDialog("Successful", "Your template has been sent for approval.", {
          type: "success",
          onConfirm: () => {
            navigate(`/home/${response.data.id}/template-tracker`);
          },
        });
      } catch (error) {
        toastError(error);
      } finally {
        setSubmitting(false);
      }
    } else {
      try {
        setSubmitting(true);
        const response = await axios.post(
          `${API_ENDPOINT}/api/update-template/${tid}`,
          payload,
          getHeader()
        );
        showDialog("Successful", "Your update has been sent for approval", {
          type: "success",
          onConfirm: () => {
            navigate(`/home/${response.data.id}/template-tracker`);
          },
        });
      } catch (error) {
        toastError(error);
      } finally {
        setSubmitting(false);
      }
    }
  }, [payload, bodyText, approveForAll]);
  const handleButtonChange = useCallback((newV) => {
    console.log(newV);
    setButtons(newV);
  }, []);
  if (loading) {
    return (
      <Box mt={4}>
        <LoadingWidget label="Loading Template to edit..." />
      </Box>
    );
  }

  /* ----------------------- render ----------------------- */
  return (
    <Container maxWidth="xl" sx={{ p: 3, bgcolor: "#e7effa" }}>
      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => {
          //setActiveTab(v)
          navigate(`/home/create-template?p=${v}`);
        }}
        sx={{ mb: 3 }}
      >
        {CATEGORIES.map((c) => (
          <Tab key={c.type} label={c.type} icon={c.icon} iconPosition="start" />
        ))}
      </Tabs>
      {activeTab < 2 && (
        <Grid container spacing={3}>
          {/* Left column (form) */}
          <Grid item xs={12} md={7}>
            {/* name + language */}
            <Paper sx={{ p: 2 }} elevation={0}>
              <Typography variant="h6" gutterBottom>
                Template name and Language
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Template Name"
                    value={templateName}
                    onChange={(e) => {
                      let t = e.target.value.slice(0, 512);
                      t = t.toLowerCase().replace(/[^a-z0-9_-]/g, "");
                      setTemplateName(t);
                    }}
                    helperText={`${templateName.length}/512`}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <LanguageAutocomplete value={lanCode} onChange={setLanCode} />
                </Grid>
              </Grid>
            </Paper>

            {/* content */}
            <Paper sx={{ p: 2, mt: 2 }} elevation={0}>
              <Typography variant="h6" gutterBottom>
                Content
              </Typography>
              <FormControl disabled size="small" fullWidth sx={{ mt: 2 }}>
                <InputLabel>Variable Type</InputLabel>
                <Select
                  value={varType}
                  label="Variable Type"
                  onChange={(e) => setVarType(e.target.value)}
                >
                  <MenuItem value="POSITIONAL">Number</MenuItem>
                  <MenuItem value="NAMED">Name</MenuItem>
                </Select>
              </FormControl>

              {/* header */}
              <Box mt={2}>
                <OptionalHeader label="Header" subLabel=" (Optional)" />
                <HeaderOptions
                  variableType={varType}
                  value={header}
                  onMediaUrl={(newV) => {
                    setPreviewUrl(newV);
                  }}
                  onChange={setHeader}
                />
              </Box>

              {/* body */}
              <Box mt={2}>
                <OptionalHeader label="Body" subLabel=" (Required)" />
                <TemplateBody
                  variableType={varType}
                  value={bodyText}
                  params={bodyParams}
                  onChange={(txt, p) => {
                    setBodyText(txt);
                    setBodyParams(p);
                  }}
                />
              </Box>

              {/* footer */}
              <Box mt={2}>
                <OptionalHeader label="Footer" subLabel=" (Optional)" />
                <TextField
                  size="small"
                  fullWidth
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value.slice(0, 60))}
                  placeholder="Footer text (max 60 chars)"
                />
              </Box>
            </Paper>

            {/* buttons */}
            <Paper sx={{ p: 2, mt: 2 }} elevation={0}>
              <OptionalHeader label="Buttons" subLabel=" (Optional)" />
              <ButtonTemplateCreator
                value={buttons}
                onChange={handleButtonChange}
              />
              <FormControlLabel
                sx={{ mt: 2 }}
                label="Approve this template for all my associated numbers"
                control={
                  <Checkbox
                    checked={approveForAll}
                    onChange={(e) => setApproveForAll(e.target.checked)}
                  />
                }
              />
            </Paper>
          </Grid>

          {/* Right column (preview + submit) */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, position: "sticky", top: 20 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6">Preview</Typography>
              </Stack>
              <TemplatePreview template={payload} previewUrl={previewUrl} />
              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <GlassButtonLoading
                  variant="contained"
                  loading={submitting}
                  endIcon={<ArrowRightAlt />}
                  onClick={handleSubmit}
                >
                  Submit Template
                </GlassButtonLoading>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
      {activeTab === 2 && <CreateAuthenticationTemplate />}
      {activeTab === 3 && <WhatsAppCarouselCreator />}
    </Container>
  );
}

export default memo(CreateSubmitTemplate);
